import { useLocalStorage } from "@vueuse/core";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import { computed, ref } from "vue";
import {
  ExtendedAddressModal,
  saveAddressExtension,
  AddressSource,
  getAddressExtensionByAddressId,
} from "@/arad/modules/address-extension";
import { updateContact } from "@/core/api/graphql/account";
import { XApiPermissions } from "@/core/enums";
import { Logger, stringifyAddress } from "@/core/utilities";
import { useUser, useUserAddresses } from "@/shared/account";
import { useFullCart, useShortCart } from "@/shared/cart";
import { BOPIS_CODE } from "@/shared/checkout/composables/useBopis";
import { setSourceMemberAddressIdForShipping } from "@/shared/checkout/composables/useCheckout";
import { AddOrUpdateCompanyAddressModal, useOrganizationAddresses } from "@/shared/company";
import { useModal } from "@/shared/modal";
import type { MemberAddressType } from "@/core/api/graphql/types";
import type { IAddressExtensionFormState, IAddressExtensionInput } from "@/arad/modules/address-extension";
import type { AnyAddressType } from "@/core/types";
import SelectAddressModal from "@/shared/checkout/components/select-address-modal.vue";

export const MAX_ADDRESSES_NUMBER = 6;
export const USER_TYPE = {
  PERSONAL: "personal",
  CORPORATE_LIMITED: "corporate-limited",
  CORPORATE: "corporate",
  ANONYMOUS: "anonymous",
} as const;

export const LOCAL_ID_PREFIX = "local-";

type UserType = (typeof USER_TYPE)[keyof typeof USER_TYPE];

/**
 * Composable for managing shipping locations and addresses
 */
export function useShipToLocation() {
  const updatingContact = ref(false);
  const { openModal, closeModal } = useModal();

  const {
    loading: loadingUser,
    organization,
    isCorporateMember,
    isAuthenticated,
    checkPermissions,
    user,
    fetchUser,
  } = useUser();

  const {
    addresses: personalAddresses,
    fetchAddresses: fetchPersonalAddresses,
    addOrUpdateAddresses: addOrUpdatePersonalAddresses,
    loading: loadingUserAddresses,
  } = useUserAddresses();

  const {
    addresses: organizationsAddresses,
    fetchAddresses: fetchOrganizationAddresses,
    loading: loadingOrganizationAddresses,
    addOrUpdateAddresses: addOrUpdateOrganizationAddresses,
  } = useOrganizationAddresses(organization.value?.id ?? "");

  const { updateShipment: updateShipmentCart, shipment: currentShipment, forceFetch: forceFetchCart } = useFullCart();
  const { cart: shortCart } = useShortCart();
  const cartShipmentId = computed(() => shortCart.value?.shipments[0]?.id);

  const localStorageKeyPrefix = "local_ship_to_";
  const userSuffix = computed(() => (isAuthenticated.value ? `_${user.value.id}` : "_anonymous"));
  const localShipToAddresses = useLocalStorage<AnyAddressType[]>(
    `${localStorageKeyPrefix}addresses${userSuffix.value}`,
    [],
  );
  const selectedLocalShipToAddressId = useLocalStorage<string | null>(
    `${localStorageKeyPrefix}selected_address_id${userSuffix.value}`,
    null,
  );

  /**
   * localStorage for address extension data indexed by local address ID.
   * This preserves extension data across cart clear operations since cart addresses get new IDs.
   * Key: local address ID (e.g., "local-uuid"), Value: extension form state
   */
  const localAddressExtensions = useLocalStorage<Record<string, IAddressExtensionFormState>>(
    `${localStorageKeyPrefix}extensions${userSuffix.value}`,
    {},
  );

  const userType = computed<UserType>(() => {
    if (!isAuthenticated.value) {
      Logger.info("useShipToLocation", "[USER TYPE] Not authenticated -> ANONYMOUS");
      return USER_TYPE.ANONYMOUS;
    }

    if (isCorporateMember.value) {
      const canEditOrganization = checkPermissions(XApiPermissions.CanEditOrganization);
      const type = canEditOrganization ? USER_TYPE.CORPORATE : USER_TYPE.CORPORATE_LIMITED;
      Logger.info(
        "useShipToLocation",
        `[USER TYPE] Corporate member, canEditOrganization=${canEditOrganization} -> ${type}`,
      );
      return type;
    }

    Logger.info("useShipToLocation", "[USER TYPE] Authenticated personal user -> PERSONAL");
    return USER_TYPE.PERSONAL;
  });

  const accountAddresses = computed<AnyAddressType[]>(() => {
    switch (userType.value) {
      case USER_TYPE.CORPORATE:
        return organizationsAddresses.value;
      case USER_TYPE.CORPORATE_LIMITED:
        return organizationsAddresses.value.length ? organizationsAddresses.value : localShipToAddresses.value;
      case USER_TYPE.PERSONAL:
        return personalAddresses.value;
      case USER_TYPE.ANONYMOUS:
        return localShipToAddresses.value;
      default:
        return [];
    }
  });

  const loading = computed(
    () =>
      loadingUser.value || loadingOrganizationAddresses.value || loadingUserAddresses.value || updatingContact.value,
  );

  const selectedAddress = computed(() => {
    switch (userType.value) {
      case USER_TYPE.PERSONAL:
      case USER_TYPE.CORPORATE:
        return accountAddresses.value.find((address) => address.id === user.value?.contact?.selectedAddressId);
      case USER_TYPE.CORPORATE_LIMITED:
        return organizationsAddresses.value.length
          ? accountAddresses.value.find((address) => address.id === user.value?.contact?.selectedAddressId)
          : localShipToAddresses.value.find((address) => address.id === selectedLocalShipToAddressId.value);
      case USER_TYPE.ANONYMOUS:
        return localShipToAddresses.value.find((address) => address.id === selectedLocalShipToAddressId.value);
      default:
        return null;
    }
  });

  function normalizeAddressToFind(address: AnyAddressType): Partial<AnyAddressType> {
    return omit(address, ["id", "addressType", "regionName"]);
  }

  function filterAddresses(addresses: AnyAddressType[], filter?: string): AnyAddressType[] {
    if (!filter) {
      return addresses;
    }

    const lowerCaseStr = filter.toLowerCase();

    return accountAddresses.value.filter((address) => {
      const combinedAddressString = stringifyAddress(address).toLowerCase();
      return combinedAddressString.includes(lowerCaseStr);
    });
  }

  function getFilteredAddresses(filter?: string): AnyAddressType[] {
    return filter ? filterAddresses(accountAddresses.value, filter) : accountAddresses.value;
  }

  function getLimitedAddresses(limit = MAX_ADDRESSES_NUMBER): AnyAddressType[] {
    return accountAddresses.value.slice(0, limit);
  }

  async function fetchAddresses() {
    switch (userType.value) {
      case USER_TYPE.ANONYMOUS:
        break;
      case USER_TYPE.PERSONAL:
        await fetchPersonalAddresses();
        break;
      case USER_TYPE.CORPORATE:
      case USER_TYPE.CORPORATE_LIMITED:
        await fetchOrganizationAddresses();
        break;
    }
  }

  async function updateContactWithAddress(address: AnyAddressType) {
    if (!user.value?.contact || !address.id) {
      return;
    }

    try {
      updatingContact.value = true;
      await updateContact({
        id: user.value.contact.id,
        firstName: user.value.contact.firstName,
        lastName: user.value.contact.lastName,
        selectedAddressId: address.id,
      });
      await fetchUser({ withBroadcast: true });
    } catch (error) {
      Logger.error("updateContactWithAddress", error);
    } finally {
      updatingContact.value = false;
    }
  }

  /**
   * Migrate extension data from localStorage (keyed by local address ID) to the new cart address.
   * This is called after updateShipmentCart when VirtoCommerce creates a new address with a new ID.
   * @param localAddressId - The local address ID (from localStorage, e.g., "local-uuid")
   * @param newCartAddressId - The new cart address ID assigned by VirtoCommerce
   */
  async function migrateExtensionToCartAddress(localAddressId: string | undefined | null, newCartAddressId: string) {
    if (!localAddressId) {
      return;
    }

    try {
      // First check if extension exists in localStorage (for local addresses)
      const localExtension = localAddressExtensions.value[localAddressId];

      if (localExtension && (localExtension.guild || localExtension.storeName || localExtension.personType)) {
        Logger.debug(
          "useShipToLocation",
          `Migrating extension from local ID ${localAddressId} to cart ID ${newCartAddressId}`,
        );
        const extensionInput: IAddressExtensionInput = {
          addressId: newCartAddressId,
          addressSource: AddressSource.Cart,
          guild: localExtension.guild || undefined,
          storeName: localExtension.storeName || undefined,
          latitude: localExtension.latitude ?? undefined,
          longitude: localExtension.longitude ?? undefined,
          personType: localExtension.personType ?? undefined,
          naturalPersonName: localExtension.naturalPersonName || undefined,
          naturalPersonFamily: localExtension.naturalPersonFamily || undefined,
          naturalPersonNationalCode: localExtension.naturalPersonNationalCode || undefined,
          nationalCardAssetId: localExtension.nationalCardAssetId ?? undefined,
          legalPersonName: localExtension.legalPersonName || undefined,
          legalPersonNationalCode: localExtension.legalPersonNationalCode || undefined,
          legalPersonEconomicalCode: localExtension.legalPersonEconomicalCode || undefined,
          businessLicenseAssetId: localExtension.businessLicenseAssetId ?? undefined,
        };
        await saveAddressExtension(extensionInput);
        return;
      }

      // Fallback: Try to get extension from Member source (for authenticated users)
      if (!localAddressId.startsWith(LOCAL_ID_PREFIX)) {
        const memberExtension = await getAddressExtensionByAddressId(localAddressId, AddressSource.Member);
        if (memberExtension && (memberExtension.guild || memberExtension.storeName || memberExtension.personType)) {
          Logger.debug(
            "useShipToLocation",
            `Migrating extension from Member ID ${localAddressId} to cart ID ${newCartAddressId}`,
          );
          const extensionInput: IAddressExtensionInput = {
            addressId: newCartAddressId,
            addressSource: AddressSource.Cart,
            guild: memberExtension.guild || undefined,
            storeName: memberExtension.storeName || undefined,
            latitude: memberExtension.latitude ?? undefined,
            longitude: memberExtension.longitude ?? undefined,
            personType: memberExtension.personType ?? undefined,
            naturalPersonName: memberExtension.naturalPersonName || undefined,
            naturalPersonFamily: memberExtension.naturalPersonFamily || undefined,
            naturalPersonNationalCode: memberExtension.naturalPersonNationalCode || undefined,
            nationalCardAssetId: memberExtension.nationalCardAssetId ?? undefined,
            legalPersonName: memberExtension.legalPersonName || undefined,
            legalPersonNationalCode: memberExtension.legalPersonNationalCode || undefined,
            legalPersonEconomicalCode: memberExtension.legalPersonEconomicalCode || undefined,
            businessLicenseAssetId: memberExtension.businessLicenseAssetId ?? undefined,
          };
          await saveAddressExtension(extensionInput);
        }
      }
    } catch (err) {
      Logger.error("useShipToLocation", "Failed to migrate extension to cart address:", err);
    }
  }

  async function selectAddress(address: AnyAddressType) {
    Logger.info("useShipToLocation", "[SELECT ADDRESS] Selecting address", {
      addressId: address.id,
      userType: userType.value,
    });

    // Store the local address ID before any operations (for extension migration)
    const localAddressId = address.id;

    // Track source member address ID for checkout extension sync
    // This ensures that when user fills billing info in checkout,
    // it can be synced back to the correct member address.
    // Only track for authenticated users with real member addresses (not local IDs).
    if (isAuthenticated.value && localAddressId && !localAddressId.startsWith(LOCAL_ID_PREFIX)) {
      setSourceMemberAddressIdForShipping(localAddressId);
    }

    switch (userType.value) {
      case USER_TYPE.PERSONAL:
      case USER_TYPE.CORPORATE: {
        void updateContactWithAddress(address);
        break;
      }
      case USER_TYPE.CORPORATE_LIMITED: {
        const isOrganizationAddress = organizationsAddresses.value.some(
          (organizationAddress) => organizationAddress.id === address.id,
        );

        if (!isOrganizationAddress) {
          selectedLocalShipToAddressId.value = address.id;
          break;
        }

        void updateContactWithAddress(address);
        break;
      }
      case USER_TYPE.ANONYMOUS: {
        selectedLocalShipToAddressId.value = address.id;
        break;
      }
    }

    if (!currentShipment.value) {
      await forceFetchCart();
    }

    const isBopis = currentShipment.value?.shipmentMethodCode === BOPIS_CODE;

    if (!isBopis) {
      await updateShipmentCart({
        id: cartShipmentId.value,
        deliveryAddress: omit(address, ["isDefault", "isFavorite"]),
      });

      // After cart update, migrate extension from local/member address to new cart address
      // VirtoCommerce generates a new address ID when adding to cart
      const newCartAddressId = currentShipment.value?.deliveryAddress?.id;
      if (newCartAddressId && newCartAddressId !== localAddressId) {
        await migrateExtensionToCartAddress(localAddressId, newCartAddressId);
      }
    }
  }

  function openSelectAddressModal() {
    openModal({
      component: SelectAddressModal,
      props: {
        addresses: accountAddresses.value,
        currentAddress: selectedAddress.value,
        isCorporateAddresses: isCorporateMember.value,
        async onResult(address?: AnyAddressType) {
          if (!address) {
            return;
          }
          await selectAddress(address);
        },
        onAddNewAddress() {
          setTimeout(() => {
            openAddOrUpdateAddressModal();
          }, 500);
        },
      },
    });
  }

  function openAddOrUpdateAddressModal() {
    Logger.info("useShipToLocation", "[OPEN MODAL] Opening add/update address modal", {
      userType: userType.value,
      isCorporateMember: isCorporateMember.value,
      isAuthenticated: isAuthenticated.value,
      userId: user.value?.id,
      organizationId: organization.value?.id,
    });

    // For corporate members, use the company address modal; otherwise use ExtendedAddressModal
    if (isCorporateMember.value) {
      Logger.info("useShipToLocation", "[OPEN MODAL] Using AddOrUpdateCompanyAddressModal (NO extension support!)");
      openModal({
        component: AddOrUpdateCompanyAddressModal,
        props: {
          async onResult(address: MemberAddressType) {
            Logger.info("useShipToLocation", "[COMPANY MODAL] Address saved (without extension)", {
              addressId: address.id,
            });
            await handleAddressAddition(address);
            closeModal();
            await selectAddress(address);
          },
        },
      });
    } else {
      Logger.info("useShipToLocation", "[OPEN MODAL] Using ExtendedAddressModal (with extension support)");
      openModal({
        component: ExtendedAddressModal,
        props: {
          addressPurpose: "shipping",
          async onResult(address: MemberAddressType, extensionState: IAddressExtensionFormState) {
            Logger.info("useShipToLocation", "[EXTENDED MODAL] Address saved with extension", {
              addressId: address.id,
              hasExtension: !!extensionState,
              extensionGuild: extensionState?.guild,
              extensionStoreName: extensionState?.storeName,
              extensionPersonType: extensionState?.personType,
            });
            await handleAddressAddition(address);

            // Save extension data if address has an ID
            if (address.id) {
              try {
                // For local addresses (anonymous/corporate-limited), save extension to localStorage
                // This allows extension data to persist across cart clear operations
                if (address.id.startsWith(LOCAL_ID_PREFIX)) {
                  localAddressExtensions.value = {
                    ...localAddressExtensions.value,
                    [address.id]: extensionState,
                  };
                  Logger.debug("useShipToLocation", `Saved extension to localStorage for local address ${address.id}`);
                }

                // Also save to backend (for Member source addresses, or as backup for local addresses)
                const extensionInput: IAddressExtensionInput = {
                  addressId: address.id,
                  addressSource: AddressSource.Member,
                  guild: extensionState.guild || undefined,
                  storeName: extensionState.storeName || undefined,
                  latitude: extensionState.latitude ?? undefined,
                  longitude: extensionState.longitude ?? undefined,
                  personType: extensionState.personType ?? undefined,
                  naturalPersonName: extensionState.naturalPersonName || undefined,
                  naturalPersonFamily: extensionState.naturalPersonFamily || undefined,
                  naturalPersonNationalCode: extensionState.naturalPersonNationalCode || undefined,
                  nationalCardAssetId: extensionState.nationalCardAssetId ?? undefined,
                  legalPersonName: extensionState.legalPersonName || undefined,
                  legalPersonNationalCode: extensionState.legalPersonNationalCode || undefined,
                  legalPersonEconomicalCode: extensionState.legalPersonEconomicalCode || undefined,
                  businessLicenseAssetId: extensionState.businessLicenseAssetId ?? undefined,
                };
                await saveAddressExtension(extensionInput);
              } catch (err) {
                Logger.error("useShipToLocation", "Failed to save address extension:", err);
              }
            }

            closeModal();
            await selectAddress(address);
          },
        },
      });
    }
  }

  async function handleAddressAddition(address: MemberAddressType) {
    Logger.info("useShipToLocation", "[HANDLE ADDITION] Starting address addition", {
      userType: userType.value,
      addressBefore: address.id,
    });

    switch (userType.value) {
      case USER_TYPE.CORPORATE: {
        Logger.info("useShipToLocation", "[HANDLE ADDITION] CORPORATE: Adding to organization addresses");

        // Capture existing IDs BEFORE save (for new address detection)
        const existingIds = new Set(organizationsAddresses.value.map((a) => a.id));
        Logger.info("useShipToLocation", "[HANDLE ADDITION] CORPORATE: Existing IDs before save:", [...existingIds]);

        await addOrUpdateOrganizationAddresses([address]);

        Logger.info(
          "useShipToLocation",
          "[HANDLE ADDITION] CORPORATE: organizationsAddresses AFTER:",
          organizationsAddresses.value.length,
        );

        // Find the newly added address (ID that didn't exist before)
        const newAddress = organizationsAddresses.value.find((a) => a.id && !existingIds.has(a.id));
        if (newAddress) {
          address.id = newAddress.id;
          Logger.info("useShipToLocation", "[HANDLE ADDITION] CORPORATE: Found NEW address with ID:", address.id);
        } else {
          Logger.warn("useShipToLocation", "[HANDLE ADDITION] CORPORATE: Could not find newly added address");
        }
        break;
      }
      case USER_TYPE.PERSONAL: {
        Logger.info("useShipToLocation", "[HANDLE ADDITION] PERSONAL: Adding to personal addresses");

        // Capture existing IDs BEFORE save (for new address detection)
        const existingIds = new Set(personalAddresses.value.map((a) => a.id));
        Logger.info("useShipToLocation", "[HANDLE ADDITION] PERSONAL: Existing IDs before save:", [...existingIds]);

        await addOrUpdatePersonalAddresses([address]);

        Logger.info(
          "useShipToLocation",
          "[HANDLE ADDITION] PERSONAL: personalAddresses AFTER:",
          personalAddresses.value.length,
        );

        // Find the newly added address (ID that didn't exist before)
        const newAddress = personalAddresses.value.find((a) => a.id && !existingIds.has(a.id));
        if (newAddress) {
          address.id = newAddress.id;
          Logger.info("useShipToLocation", "[HANDLE ADDITION] PERSONAL: Found NEW address with ID:", address.id);
        } else {
          Logger.warn("useShipToLocation", "[HANDLE ADDITION] PERSONAL: Could not find newly added address");
        }
        break;
      }
      case USER_TYPE.CORPORATE_LIMITED:
      case USER_TYPE.ANONYMOUS: {
        address.id = `${LOCAL_ID_PREFIX}${crypto.randomUUID()}`;
        localShipToAddresses.value = [...localShipToAddresses.value, address];
        Logger.info(
          "useShipToLocation",
          `[HANDLE ADDITION] ${userType.value}: Local address created with ID:`,
          address.id,
        );
        break;
      }
    }
  }

  return {
    accountAddresses,
    loading,
    selectedAddress,
    organizationsAddresses,
    localAddressExtensions,

    fetchAddresses,
    selectAddress,
    migrateExtensionToCartAddress,

    getFilteredAddresses,
    getLimitedAddresses,

    openSelectAddressModal,
    openAddOrUpdateAddressModal,
  };
}
